import { expenseGrossSpend } from "../spend";

/** Pure budget helpers (dashboard / expenses). Amounts in EUR. */

export function sumExpensesAmounts(
  expenses: ReadonlyArray<{ amount?: number | null | undefined; amount_gross?: number | null | undefined }>
): number {
  return expenses.reduce((s, e) => s + expenseGrossSpend(e), 0);
}

/**
 * Sum recorded expenses per phase. Prefer expense.phase_id; if missing, resolve via task → phase map.
 */
export function sumExpensesByPhaseId(
  expenses: ReadonlyArray<{
    amount?: number | null | undefined;
    amount_gross?: number | null | undefined;
    phase_id: string | null;
    task_id: string | null;
  }>,
  taskPhaseById?: ReadonlyMap<string, string | null | undefined>
): Map<string, number> {
  const by = new Map<string, number>();
  for (const e of expenses) {
    const amount = expenseGrossSpend(e);
    if (!amount) continue;
    const phaseId = e.phase_id ?? (e.task_id ? (taskPhaseById?.get(e.task_id) ?? null) : null);
    if (!phaseId) continue;
    by.set(phaseId, (by.get(phaseId) ?? 0) + amount);
  }
  return by;
}

/** Sum recorded expenses per task_id (skips rows without task). */
export function sumExpensesByTaskId(
  expenses: ReadonlyArray<{
    amount?: number | null | undefined;
    amount_gross?: number | null | undefined;
    task_id: string | null;
  }>
): Map<string, number> {
  const by = new Map<string, number>();
  for (const e of expenses) {
    if (!e.task_id) continue;
    const amount = expenseGrossSpend(e);
    if (!amount) continue;
    by.set(e.task_id, (by.get(e.task_id) ?? 0) + amount);
  }
  return by;
}

function plannedAmount(planned: number | null | undefined): number {
  return typeof planned === "number" && Number.isFinite(planned) ? planned : 0;
}

/**
 * Sum `tasks.budget_planned` per phase. A fázisnak nincs saját tervezett mezője:
 * a fázis terve mindig a feladatai összege, hogy egy igazság legyen.
 */
export function sumPlannedByPhaseId(
  tasks: ReadonlyArray<{ phase_id: string | null; budget_planned: number | null | undefined }>
): Map<string, number> {
  const by = new Map<string, number>();
  for (const t of tasks) {
    if (!t.phase_id) continue;
    const planned = plannedAmount(t.budget_planned);
    if (!planned) continue;
    by.set(t.phase_id, (by.get(t.phase_id) ?? 0) + planned);
  }
  return by;
}

/**
 * Sum `tasks.budget_planned` per task, az alfeladatokat a szülőbe göngyölítve.
 * Egy szülő sor így a teljes ág tervét mutatja, nem csak a sajátját.
 */
export function sumPlannedByTaskId(
  tasks: ReadonlyArray<{
    id: string;
    parent_task_id: string | null;
    budget_planned: number | null | undefined;
  }>
): Map<string, number> {
  const parentById = new Map(tasks.map((t) => [t.id, t.parent_task_id] as const));
  const by = new Map<string, number>();

  for (const task of tasks) {
    const planned = plannedAmount(task.budget_planned);
    if (!planned) continue;
    // Fel a gyökérig; a `seen` a hibás adatból eredő körökre védelem.
    const seen = new Set<string>();
    let current: string | null | undefined = task.id;
    while (current && !seen.has(current)) {
      seen.add(current);
      by.set(current, (by.get(current) ?? 0) + planned);
      current = parentById.get(current) ?? null;
    }
  }
  return by;
}

export function remainingBudget(budgetTotal: number, spent: number): number {
  return Math.max(0, budgetTotal - spent);
}

export function budgetUsagePercent(budgetTotal: number, spent: number): number {
  if (!(budgetTotal > 0)) return 0;
  return Math.min(100, Math.round((spent / budgetTotal) * 100));
}

export function phaseBudgetUsagePercent(planned: number, actual: number): number {
  if (!(planned > 0)) return 0;
  return Math.min(100, Math.round((actual / planned) * 100));
}

export function isOverBudget(planned: number, actual: number): boolean {
  return planned > 0 && actual > planned;
}

/**
 * Linear projection: if spending continues at `currentBurnPerDay` EUR/day until `deadline`,
 * will total exceed `budgetTotal`? Uses whole days from `today` to deadline (inclusive ceiling).
 */
export function willExceedBudgetLinear(args: {
  budgetTotal: number;
  spentSoFar: number;
  currentBurnPerDay: number;
  today: Date;
  deadline: Date;
}): boolean {
  const { budgetTotal, spentSoFar, currentBurnPerDay, today, deadline } = args;
  const ms = deadline.getTime() - today.getTime();
  const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  const projected = spentSoFar + currentBurnPerDay * days;
  return projected > budgetTotal;
}
